import React, { useState } from "react";
import { Form, Row, Col, Button, Card } from "react-bootstrap";
import { FaDollarSign, FaSave, FaTimes, FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";

const PricingForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    item_id: "",
    cost_price: "",
    selling_price: "",
    markup_percentage: "",
    discount_percentage: "",
    effective_date: "",
    tax_percentage: ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Pricing Data:", formData);
    toast.success("Pricing updated successfully!");
    setFormData({
      item_id: "",
      cost_price: "",
      selling_price: "",
      markup_percentage: "",
      discount_percentage: "",
      effective_date: "",
      tax_percentage: ""
    });
    if (onBack) onBack();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate selling price based on cost price and markup
  const calculateSellingPrice = () => {
    const cost = parseFloat(formData.cost_price) || 0;
    const markup = parseFloat(formData.markup_percentage) || 0;
    if (cost && markup) {
      const sellingPrice = cost + (cost * markup / 100);
      setFormData(prev => ({ ...prev, selling_price: sellingPrice.toFixed(2) }));
    }
  };

  // Calculate markup based on cost and selling price
  const calculateMarkup = () => {
    const cost = parseFloat(formData.cost_price) || 0;
    const selling = parseFloat(formData.selling_price) || 0;
    if (cost && selling) {
      const markup = ((selling - cost) / cost) * 100;
      setFormData(prev => ({ ...prev, markup_percentage: markup.toFixed(2) }));
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-dark text-white d-flex align-items-center">
        <FaDollarSign className="me-2" />
        <h5 className="mb-0">Add/Modify Pricing</h5>
        {onBack && (
          <Button
            variant="light"
            size="sm"
            className="ms-auto"
            onClick={onBack}
          >
            <FaArrowLeft className="me-1" />
            Back
          </Button>
        )}
      </Card.Header>
      <Card.Body className="p-4">
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Item *</Form.Label>
                <Form.Select
                  value={formData.item_id}
                  onChange={(e) => handleChange('item_id', e.target.value)}
                  required
                  className="form-select-lg"
                >
                  <option value="">Select Item</option>
                  <option value="1">Rice</option>
                  <option value="2">Chicken</option>
                  <option value="3">Vegetables</option>
                  <option value="4">Oil</option>
                  <option value="5">Spices</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => handleChange('effective_date', e.target.value)}
                  className="form-control-lg"
                  defaultValue={new Date().toISOString().split('T')[0]}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Cost Price *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.cost_price}
                  onChange={(e) => handleChange('cost_price', e.target.value)}
                  onBlur={calculateSellingPrice}
                  required
                  className="form-control-lg"
                  placeholder="Enter cost price"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Selling Price *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.selling_price}
                  onChange={(e) => handleChange('selling_price', e.target.value)}
                  onBlur={calculateMarkup}
                  required
                  className="form-control-lg"
                  placeholder="Enter selling price"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Markup Percentage</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.markup_percentage}
                  onChange={(e) => handleChange('markup_percentage', e.target.value)}
                  onBlur={calculateSellingPrice}
                  className="form-control-lg"
                  placeholder="Auto calculated"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Discount Percentage</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.discount_percentage}
                  onChange={(e) => handleChange('discount_percentage', e.target.value)}
                  className="form-control-lg"
                  placeholder="Enter discount %"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Tax Percentage</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.tax_percentage}
                  onChange={(e) => handleChange('tax_percentage', e.target.value)}
                  className="form-control-lg"
                  placeholder="Enter tax %"
                />
              </Form.Group>
            </Col>
          </Row>
          
          {/* Price Summary */}
          {formData.cost_price && formData.selling_price && (
            <Card className="bg-light mb-4">
              <Card.Body>
                <h6 className="fw-bold mb-2">Price Summary</h6>
                <Row>
                  <Col>Cost Price: <strong>₹{formData.cost_price}</strong></Col>
                  <Col>Selling Price: <strong>₹{formData.selling_price}</strong></Col>
                  <Col>Profit: <strong>₹{(parseFloat(formData.selling_price) - parseFloat(formData.cost_price)).toFixed(2)}</strong></Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          <div className="d-flex justify-content-end gap-3">
            {onBack && (
              <Button 
                variant="outline-secondary" 
                size="lg"
                onClick={onBack}
              >
                <FaTimes className="me-1" />
                Cancel
              </Button>
            )}
            <Button 
              variant="dark" 
              type="submit" 
              size="lg"
              className="px-4"
            >
              <FaSave className="me-1" />
              Update Pricing
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default PricingForm;
